import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Navigate, useNavigate } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { getErrorMessage } from '@/api/http/getErrorMessage'
import { useLogout } from '@/features/auth/api'
import { captureAccountCreationStarted } from '@/lib/analytics'
import { useSessionStore } from '@/stores/useSessionStore'
import {
  clearWebSurveyOnboardingSession,
  readWebSurveyOnboardingSession,
  readWebSurveyPhoneVerified,
  readWebSurveySurveyFirst,
  webSurveyMobileOnboardingPath,
} from '@/features/auth/lib/constants'
import brushfireLogo from '@/features/auth/assets/login/brushfire-logo.png'
import { useCheckWebSurveyEmail, useCreateWebSurveyAccount } from '../api/instrument-onboarding-mutations'
import {
  buildWebSurveyAccountSchema,
  WEB_SURVEY_PASSWORD_REQUIREMENTS_ISSUE,
  type WebSurveyAccountFormData,
} from '../lib/web-survey-account-schema'
import {
  isWebSurveyRegistrationTypeEnabled,
  WEB_SURVEY_DEFAULT_PARTY,
} from '../lib/web-survey-registration-type'
import {
  SP_PASSWORD_RULE_ORDER,
  evaluateSpPasswordRules,
} from '@/features/auth/lib/password-rules'
import { WebSurveyPasswordChecklist } from '../components/WebSurveyPasswordChecklist'
import { WebSurveyAccountConsent } from '../components/WebSurveyAccountConsent'
import {
  WebSurveyPasswordField,
  WebSurveyRegistrationSelect,
  WebSurveyTextField,
} from '../components/WebSurveyAccountFields'
import {
  webSurveyAccountFormSx,
  webSurveyAccountHeadingSx,
  webSurveyAccountLogoBlockSx,
  webSurveyAccountLogoSx,
  webSurveyAccountNameFieldSx,
  webSurveyAccountNameRowSx,
  webSurveyAccountPageSx,
  webSurveyAccountSignInButtonSx,
  webSurveyAccountSignInRowSx,
  webSurveyAccountSubmitSx,
  webSurveyAccountSubtitleSx,
} from '../styles/webSurveyAccountStyles'
import { SignupShareThemeProvider } from '../styles/signupShareTheme'
import { SurveySignupStepper } from '../components/SurveySignupStepper'
import { WebSurveyLegalModal } from '../components/WebSurveyLegalModal'
import { marketingPrivacyUrl, marketingTermsUrl } from '../lib/marketing-legal-urls'
import {
  PRIVACY_BODY_PARAGRAPHS,
  PRIVACY_LAST_UPDATED,
  TERMS_BODY_PARAGRAPHS,
  TERMS_LAST_UPDATED,
} from '../lib/policy-content'

const PASSWORDS_DO_NOT_MATCH = 'Passwords do not match'

const lightBorderInputSx = {
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#EDEDED' },
} as const

function passwordMeetsAllRules(password: string | undefined): boolean {
  if (!password) {
    return false
  }
  const checks = evaluateSpPasswordRules(password)
  return SP_PASSWORD_RULE_ORDER.every((key) => checks[key])
}

function confirmPasswordFieldError(
  password: string | undefined,
  confirmPassword: string | undefined,
  schemaMessage: string | undefined,
): string | undefined {
  if ((password?.length ?? 0) > 0 && (confirmPassword?.length ?? 0) > 0 && password !== confirmPassword) {
    return PASSWORDS_DO_NOT_MATCH
  }
  if ((password?.length ?? 0) > 0 && !passwordMeetsAllRules(password)) {
    return WEB_SURVEY_PASSWORD_REQUIREMENTS_ISSUE
  }
  return schemaMessage
}

export function WebSurveyAccountPage() {
  useDocumentTitle('Create Your Account')
  const navigate = useNavigate()
  const createAccount = useCreateWebSurveyAccount()
  const emailCheck = useCheckWebSurveyEmail()
  const logout = useLogout()
  const sessionId = readWebSurveyOnboardingSession()
  const surveyFirst = readWebSurveySurveyFirst()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false)
  const [legalModal, setLegalModal] = useState<'terms' | 'privacy' | null>(null)
  const [emailTaken, setEmailTaken] = useState<string | null>(null)
  const [emailAvailable, setEmailAvailable] = useState(false)
  const sessionRegistrationType = useSessionStore(
    (s) => s.user?.webSurveyRegistrationTypeEnabled,
  )
  const registrationTypeEnabled =
    typeof sessionRegistrationType === 'boolean'
      ? sessionRegistrationType
      : isWebSurveyRegistrationTypeEnabled()
  const accountSchema = buildWebSurveyAccountSchema(registrationTypeEnabled)
  const {
    register,
    control,
    handleSubmit,
    watch,
    trigger,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<WebSurveyAccountFormData>({
    resolver: zodResolver(accountSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      registeredPoliticalParty: WEB_SURVEY_DEFAULT_PARTY,
    },
  })

  const password = watch('password')
  const confirmPassword = watch('confirmPassword')
  const confirmError = confirmPasswordFieldError(
    password,
    confirmPassword,
    errors.confirmPassword?.message,
  )
  const passwordsDoNotMatch = confirmError === PASSWORDS_DO_NOT_MATCH
  const sessionReady = Boolean(sessionId) && readWebSurveyPhoneVerified()

  useEffect(() => {
    if (sessionReady) captureAccountCreationStarted()
  }, [sessionReady])

  if (!sessionId) {
    return (
      <Box sx={webSurveyAccountPageSx}>
        <Alert severity="error" role="alert">
          Your survey session expired. Please use the link from your SMS to continue.
        </Alert>
      </Box>
    )
  }

  if (!readWebSurveyPhoneVerified()) {
    return <Navigate to={webSurveyMobileOnboardingPath()} replace />
  }

  const onSubmit = handleSubmit(async (values) => {
    try {
      await createAccount.mutateAsync({
        onboardingSessionId: sessionId,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        confirmPassword: values.confirmPassword,
        registeredPoliticalParty: registrationTypeEnabled
          ? values.registeredPoliticalParty || WEB_SURVEY_DEFAULT_PARTY
          : WEB_SURVEY_DEFAULT_PARTY,
        acceptedTerms: true,
        acceptedPrivacy: true,
      })
      clearWebSurveyOnboardingSession()
      navigate(surveyFirst ? '/rewards?from=signup-survey' : '/', { replace: true })
    } catch {
      /* mutation error rendered below */
    }
  })

  const pending = isSubmitting || createAccount.isPending
  const hasValidationErrors = Object.keys(errors).length > 0
  const consentsReady = acceptedTerms && acceptedPrivacy
  const emailReady = !surveyFirst || emailAvailable
  const submitDisabled =
    pending || hasValidationErrors || passwordsDoNotMatch || !consentsReady || !emailReady || Boolean(emailTaken)

  return (
    <SignupShareThemeProvider>
      <Box data-testid="web-survey-account-page" sx={webSurveyAccountPageSx}>
        <Box sx={webSurveyAccountLogoBlockSx} data-testid="web-survey-account-logo-wrap">
          <Box
            component="img"
            src={brushfireLogo}
            alt="Brushfire"
            sx={webSurveyAccountLogoSx}
            data-testid="web-survey-account-logo"
          />
        </Box>
        <SurveySignupStepper activeStep={2} compactLabels />
        <Typography component="h1" sx={webSurveyAccountHeadingSx}>
          Create Your Account
        </Typography>
        <Typography component="p" sx={webSurveyAccountSubtitleSx}>
          It only takes a few minutes to create your Brushfire account and claim your reward.
        </Typography>

        <Box component="form" onSubmit={onSubmit} sx={webSurveyAccountFormSx} noValidate>
          {createAccount.isError ? (
            <Alert severity="error" role="alert" sx={{ width: '100%' }}>
              {getErrorMessage(createAccount.error)}
            </Alert>
          ) : null}

          <Box sx={webSurveyAccountNameRowSx} data-testid="web-survey-name-row">
            <WebSurveyTextField
              id="web-survey-first-name"
              label="First Name"
              required
              autoComplete="given-name"
              placeholder="First name"
              errorMessage={errors.firstName?.message}
              testId="web-survey-first-name"
              fieldSx={webSurveyAccountNameFieldSx}
              inputSx={lightBorderInputSx}
              register={register('firstName')}
            />
            <WebSurveyTextField
              id="web-survey-last-name"
              label="Last Name"
              required
              autoComplete="family-name"
              placeholder="Last name"
              errorMessage={errors.lastName?.message}
              testId="web-survey-last-name"
              fieldSx={webSurveyAccountNameFieldSx}
              inputSx={lightBorderInputSx}
              register={register('lastName')}
            />
          </Box>

          <WebSurveyTextField
            id="web-survey-email"
            label="Email Address"
            required
            type="email"
            autoComplete="email"
            errorMessage={errors.email?.message ?? emailTaken ?? undefined}
            testId="web-survey-email"
            inputSx={lightBorderInputSx}
            register={register('email', {
              onChange: () => {
                setEmailAvailable(false)
                setEmailTaken(null)
              },
              onBlur: () => {
                const email = getValues('email')?.trim() ?? ''
                if (!surveyFirst || !email.includes('@')) {
                  setEmailTaken(null)
                  return
                }
                void emailCheck.mutateAsync(email).then(
                  () => {
                    setEmailTaken(null)
                    setEmailAvailable(true)
                  },
                  (err) => {
                    setEmailAvailable(false)
                    setEmailTaken(getErrorMessage(err))
                  },
                )
              },
            })}
          />

          {registrationTypeEnabled ? (
            <WebSurveyRegistrationSelect control={control} errors={errors} />
          ) : null}

          <WebSurveyPasswordField
            id="web-survey-password"
            label="Password"
            placeholder="Create a password"
            autoComplete="new-password"
            errorMessage={
              errors.password?.message === WEB_SURVEY_PASSWORD_REQUIREMENTS_ISSUE
                ? undefined
                : errors.password?.message
            }
            testId="web-survey-password"
            visible={showPassword}
            onToggleVisibility={() => setShowPassword((v) => !v)}
            register={register('password', {
              onChange: () => {
                if (getValues('confirmPassword')) {
                  void trigger('confirmPassword')
                }
              },
            })}
          />

          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <WebSurveyPasswordField
              id="web-survey-confirm-password"
              label="Confirm Password"
              placeholder="Confirm your password"
              autoComplete="new-password"
              errorMessage={confirmError}
              testId="web-survey-confirm-password"
              visible={showConfirmPassword}
              onToggleVisibility={() => setShowConfirmPassword((v) => !v)}
              register={register('confirmPassword')}
            />
            <WebSurveyPasswordChecklist password={password} />
          </Box>

          <WebSurveyAccountConsent
            accepted={consentsReady}
            termsHref={marketingTermsUrl()}
            privacyHref={marketingPrivacyUrl()}
            onAcceptedChange={(checked) => {
              if (!checked) {
                setAcceptedTerms(false)
                setAcceptedPrivacy(false)
              }
            }}
            onStartConsentFlow={() => {
              setAcceptedTerms(false)
              setAcceptedPrivacy(false)
              setLegalModal('terms')
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disableElevation
            disabled={submitDisabled}
            data-testid="web-survey-account-submit"
            sx={webSurveyAccountSubmitSx}
          >
            {pending ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
          </Button>

          <Typography component="p" sx={webSurveyAccountSignInRowSx}>
            Already have an account?{' '}
            <Button
              type="button"
              variant="text"
              disableRipple
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
              data-testid="web-survey-account-sign-in"
              sx={webSurveyAccountSignInButtonSx}
            >
              Sign In
            </Button>
          </Typography>
        </Box>
      </Box>
      {legalModal ? (
        <WebSurveyLegalModal
          key={legalModal}
          open
          title={legalModal === 'terms' ? 'Terms of Use' : 'Privacy Policy'}
          paragraphs={legalModal === 'terms' ? TERMS_BODY_PARAGRAPHS : PRIVACY_BODY_PARAGRAPHS}
          lastUpdated={legalModal === 'terms' ? TERMS_LAST_UPDATED : PRIVACY_LAST_UPDATED}
          acceptMode
          onClose={() => {
            setAcceptedTerms(false)
            setAcceptedPrivacy(false)
            setLegalModal(null)
          }}
          onAccept={() => {
            if (legalModal === 'terms') {
              setAcceptedTerms(true)
              setLegalModal('privacy')
              return
            }
            setAcceptedPrivacy(true)
            setLegalModal(null)
          }}
          onDecline={() => {
            setAcceptedTerms(false)
            setAcceptedPrivacy(false)
            setLegalModal(null)
          }}
        />
      ) : null}
    </SignupShareThemeProvider>
  )
}
